// Hero slide content entrance reveal.
//
// As each hero slide becomes active, its content (eyebrow → heading → body → buttons) fades and
// rises in a short stagger — a premium "build-on" that gives the homepage movement without touching
// the layout. Pure CSS does the animation (see hero-slide-frame.liquid); this module only:
//   1. marks the slideshow `hero-reveal-ready` so the CSS initial-hidden state engages — and ONLY
//      when motion is allowed, so no-JS / reduced-motion users get fully-visible content (no flash),
//   2. toggles `is-revealing` on the active slide's inner on each `slideshow:select` (the same
//      bubbling event the autoplay timing uses), replaying the stagger on every (re)entry.
//
// Compositor-only (opacity/transform), no layout reads, no per-frame work — negligible INP cost.

(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  const reveal = (slide, instant) => {
    if (!slide) return;
    const inner = slide.querySelector('.hero-slide__inner');
    if (!inner) return;
    // Initial paint (instant): add `is-revealing` in the SAME synchronous task as `hero-reveal-ready`
    // with NO forced reflow between, so the browser coalesces both class changes into a single style
    // recalc and the at-load slide computes straight to opacity:1 — it is never painted hidden. This
    // is the LCP guard: without it, the deferred module would hide the already-painted above-the-fold
    // text and re-fade it in, registering a late LCP. Trade-off: the at-load slide has no entrance
    // animation (it's just there); slides 2+ keep the staggered build-on below.
    if (instant) {
      inner.classList.add('is-revealing');
      return;
    }
    // Re-entry: restart the transition (drop the class, force a reflow, re-add) so the stagger replays.
    inner.classList.remove('is-revealing');
    void inner.offsetWidth;
    inner.classList.add('is-revealing');
  };

  const bind = (slideshow) => {
    if (slideshow.dataset.heroRevealBound) return;
    slideshow.dataset.heroRevealBound = '1';

    // Honor reduced motion: leave content static and visible (don't add the ready class).
    if (reduce.matches) return;

    slideshow.classList.add('hero-reveal-ready');

    // INVARIANT: do not read layout (offsetWidth/getBoundingClientRect/getComputedStyle) between the
    // line above and reveal(first, true) below. A forced reflow here would flush a recalc against
    // `hero-reveal-ready`-only, paint the first slide at opacity:0, and reintroduce the late-LCP bug.
    // Reveal whichever slide is active at load (the engine marks it aria-hidden="false").
    const first =
      slideshow.querySelector('slideshow-slide[aria-hidden="false"]') ||
      slideshow.querySelector('slideshow-slide');
    reveal(first, true);

    slideshow.addEventListener('slideshow:select', (event) => {
      reveal(event.detail && event.detail.slide);
    });
  };

  const init = () => {
    document.querySelectorAll('slideshow-component.hero-slider').forEach(bind);
  };

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // Theme editor: a section re-render swaps in a fresh slideshow element (the bind flag is lost),
  // so re-bind any hero sliders inside the reloaded section.
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('slideshow-component.hero-slider').forEach(bind);
  });
})();
